import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen, Eye, EyeOff, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage({ auth }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showChangeCredentials, setShowChangeCredentials] = useState(false);
  const [form, setForm] = useState({ username: "", password: "" });
  const [newCredentials, setNewCredentials] = useState({ newUsername: "", newPassword: "", confirmPassword: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Errore di login");
      }
      
      auth.login(data.token, data.user);
      
      toast.success("Accesso effettuato!");
      
      if (data.user.first_login) {
        toast.info("Primo accesso! Ti consigliamo di cambiare le credenziali dal menu impostazioni.");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeCredentials = async (e) => {
    e.preventDefault();
    if (newCredentials.newPassword !== newCredentials.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    if (newCredentials.newPassword.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/change-credentials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          new_password: newCredentials.newPassword,
          new_username: newCredentials.newUsername || null
        })
      });
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Errore");
      }
      
      auth.setUser({ ...auth.user, first_login: false, username: newCredentials.newUsername || auth.user.username });
      toast.success("Credenziali aggiornate!");
      navigate("/admin");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top Section with Large Logo */}
      <div className="flex-1 flex items-center justify-center pt-8 pb-4">
        <div className="text-center">
          <img 
            src="/sdg-logo-main.png" 
            alt="Sistemi Digitali Group" 
            className="w-80 md:w-[450px] h-auto mx-auto"
          />
        </div>
      </div>
      
      {/* Bottom Section with Login Form */}
      <div className="w-full max-w-md mx-auto px-4 pb-12">

        {!showChangeCredentials ? (
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl">Accedi</CardTitle>
              <CardDescription>
                Inserisci le tue credenziali per accedere al pannello
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Nome utente</Label>
                  <Input
                    id="username"
                    data-testid="login-username-input"
                    placeholder="admin"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full" 
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Accedi
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl">Modifica credenziali</CardTitle>
              <CardDescription>
                È il tuo primo accesso. Imposta nuove credenziali sicure.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangeCredentials} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newUsername">Nuovo nome utente (opzionale)</Label>
                  <Input
                    id="newUsername"
                    data-testid="new-username-input"
                    placeholder={auth.user?.username || "Lascia vuoto per mantenere"}
                    value={newCredentials.newUsername}
                    onChange={(e) => setNewCredentials({ ...newCredentials, newUsername: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nuova password *</Label>
                  <Input
                    id="newPassword"
                    data-testid="new-password-input"
                    type="password"
                    placeholder="Minimo 6 caratteri"
                    value={newCredentials.newPassword}
                    onChange={(e) => setNewCredentials({ ...newCredentials, newPassword: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Conferma password *</Label>
                  <Input
                    id="confirmPassword"
                    data-testid="confirm-password-input"
                    type="password"
                    placeholder="Ripeti la password"
                    value={newCredentials.confirmPassword}
                    onChange={(e) => setNewCredentials({ ...newCredentials, confirmPassword: e.target.value })}
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full" 
                  disabled={loading}
                  data-testid="change-credentials-submit-btn"
                >
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Salva e continua
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Footer */}
      <div className="text-center pb-6">
        <p className="text-gray-400 text-xs">© 2025 Sistemi Digitali Group</p>
      </div>
    </div>
  );
}
